import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Spinner,
  Badge,
  Table,
  Form,
  Button,
  Modal,
  Pagination,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useOutletContext } from "react-router-dom";
import Header from "../components/Header";
import MonitorBlacklistModal from "../components/MonitorBlacklistModal";
import MonitorCategoriesModal from "../components/MonitorCategoriesModal";
import { getHost } from "../utils/utils";
import {
  FiPackage,
  FiSearch,
  FiCheckCircle,
  FiXCircle,
  FiTrash2,
  FiPlus,
  FiCopy,
  FiExternalLink,
  FiX,
  FiRefreshCw,
  FiShield,
  FiFolder,
  FiArchive,
  FiRotateCcw,
  FiCheck,
} from "react-icons/fi";

const getToken = () => localStorage.getItem("jwt");
const PAGE_SIZE = 50;

function parseDate(ts) {
  if (!ts) return null;
  // Unix timestamp as string (e.g. "1742248386")
  if (typeof ts === "string" && /^\d+$/.test(ts)) {
    return new Date(parseInt(ts) * 1000);
  }
  // Unix timestamp as number
  if (typeof ts === "number" && ts < 1e12) {
    return new Date(ts * 1000);
  }
  // ISO string or JS timestamp
  const date = new Date(ts);
  return isNaN(date.getTime()) ? null : date;
}

function formatRelativeTime(ts) {
  const date = parseDate(ts);
  if (!date) return "—";
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return "just now";
  const mins = Math.floor(diff / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatDate(ts) {
  const date = parseDate(ts);
  if (!date) return "—";
  return date.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

const Copiable = ({ text, children, wrap }) => {
  const [copied, setCopied] = useState(false);
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  if (wrap) {
    return (
      <span
        role="button"
        onClick={copy}
        title={copied ? "Copied!" : "Click to copy"}
        style={{
          cursor: "pointer",
          display: "flex",
          alignItems: "flex-start",
          gap: 4,
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <span
          style={{
            minWidth: 0,
            flex: 1,
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {children || text}
        </span>
        <FiCopy style={{ fontSize: 10, color: copied ? "#00E98E" : "#555", flexShrink: 0, marginTop: 3 }} />
      </span>
    );
  }
  return (
    <span
      role="button"
      onClick={copy}
      title={copied ? "Copied!" : "Click to copy"}
      style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
    >
      {children || text}
      <FiCopy style={{ fontSize: 10, color: copied ? "#00E98E" : "#555", flexShrink: 0 }} />
    </span>
  );
};

const SummaryCard = ({ icon, label, value, color }) => (
  <Col lg={3} md={4} sm={6} xs={6} className="mb-3">
    <Card className="border border-dark bg-transparent h-100">
      <Card.Body className="p-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          {icon}
          <span className="text-white-65 fs-12">{label}</span>
        </div>
        <h4 className="fw-600 mb-0" style={{ color: color || "#ADADAD" }}>
          {value}
        </h4>
      </Card.Body>
    </Card>
  </Col>
);

const Toast = ({ message, type, onClose }) => (
  <div
    style={{
      position: "fixed",
      bottom: 24,
      right: 24,
      zIndex: 9999,
      padding: "10px 16px",
      borderRadius: 8,
      background: type === "error" ? "#FF444433" : type === "success" ? "#00E98E22" : "#ffffff14",
      border: `1px solid ${type === "error" ? "#FF4444" : type === "success" ? "#00E98E" : "#333"}`,
      color: type === "error" ? "#FF4444" : type === "success" ? "#00E98E" : "#ADADAD",
      fontSize: 13,
      display: "flex",
      alignItems: "center",
      gap: 8,
    }}
  >
    {message}
    <FiX style={{ cursor: "pointer", fontSize: 14 }} onClick={onClose} />
  </div>
);

const ConfirmModal = ({ show, title, message, onConfirm, onCancel, confirmLabel = "Confirm", variant = "danger", children }) => (
  <Modal show={show} onHide={onCancel} centered size="sm">
    <Modal.Body className="p-4">
      <p className="text-main fw-600 fs-14 mb-2">{title}</p>
      <p className="text-white-65 fs-13 mb-0">{message}</p>
      {children}
    </Modal.Body>
    <Modal.Footer className="border-0 pt-0">
      <Button variant="secondary" size="sm" onClick={onCancel} className="rounded-3 px-3 fs-12">
        Cancel
      </Button>
      <Button
        variant={variant}
        size="sm"
        onClick={onConfirm}
        className="rounded-3 px-3 fs-12"
      >
        {confirmLabel}
      </Button>
    </Modal.Footer>
  </Modal>
);

const AdminProducts = () => {
  const { toggleSidebar } = useOutletContext();

  // Monitor selection
  const [monitors, setMonitors] = useState([]);
  const [productCounts, setProductCounts] = useState({});
  const [selectedMonitor, setSelectedMonitor] = useState("");
  const [monitorSearch, setMonitorSearch] = useState("");
  const [showMonitorDropdown, setShowMonitorDropdown] = useState(false);
  const [loadingMonitors, setLoadingMonitors] = useState(true);
  const monitorDropdownRef = useRef(null);

  // Products
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [stats, setStats] = useState({ instock: 0, outofstock: 0, whitelisted: 0, deleted: 0 });
  const [showDeleted, setShowDeleted] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Filters & pagination
  const [search, setSearch] = useState("");
  const [instockFilter, setInstockFilter] = useState("");
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(totalProducts / PAGE_SIZE);

  // Add product modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBlacklistModal, setShowBlacklistModal] = useState(false);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [newPid, setNewPid] = useState("");
  const [newMaxPrice, setNewMaxPrice] = useState("");
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [adding, setAdding] = useState(false);

  // Regional siblings (same family + same category)
  const [siblings, setSiblings] = useState([]);
  const [applyFamily, setApplyFamily] = useState(false);
  const [selectedSiblingIds, setSelectedSiblingIds] = useState([]);
  const familyIds = applyFamily ? selectedSiblingIds : [];

  // Expanded sizes
  const [expandedSizes, setExpandedSizes] = useState(null);

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const toggleSelect = (pid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === products.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(products.map((p) => p.pid)));
    }
  };

  const handleBulkDelete = () => {
    if (selected.size === 0) return;
    showConfirm(
      "Bulk delete",
      `Delete ${selected.size} product${selected.size > 1 ? "s" : ""} from tracking? They move to the Deleted view, so the monitor won't re-add them; restore them from there.`,
      async (monitorIds) => {
        setConfirmState(null);
        setDeleting(true);
        try {
          const pids = [...selected];
          const res = await fetch(
            `${getHost()}/api/admin/monitors/${selectedMonitor}/products/bulk`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${getToken()}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                pids,
                ...(monitorIds.length > 0 ? { monitor_ids: monitorIds } : {}),
              }),
            },
          );
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || "Bulk delete failed");
          showToast(`Deleted ${pids.length} products${familyMsg(data)}`);
          setSelected(new Set());
          fetchProducts(selectedMonitor, page, search, instockFilter, showDeleted);
        } catch (error) {
          showToast(error.message, "error");
        } finally {
          setDeleting(false);
        }
      },
      "Delete",
      "danger",
      true,
    );
  };

  const restoreProducts = async (pids, monitorIds) => {
    const res = await fetch(
      `${getHost()}/api/admin/monitors/${selectedMonitor}/products/restore`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          pids,
          ...(monitorIds.length > 0 ? { monitor_ids: monitorIds } : {}),
        }),
      },
    );
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Restore failed");
    return data;
  };

  const handleRestore = (pids) => {
    if (siblings.length === 0) {
      runRestore(pids, []);
      return;
    }
    showConfirm(
      "Restore",
      `Restore ${pids.length} product${pids.length > 1 ? "s" : ""} to tracking?`,
      (monitorIds) => {
        setConfirmState(null);
        runRestore(pids, monitorIds);
      },
      "Restore",
      "success",
      true,
    );
  };

  const runRestore = async (pids, monitorIds) => {
    setDeleting(true);
    try {
      const data = await restoreProducts(pids, monitorIds);
      const restored = data.restored || 0;
      showToast(`Restored ${restored} product${restored === 1 ? "" : "s"}${familyMsg(data)}`);
      setSelected(new Set());
      fetchProducts(selectedMonitor, page, search, instockFilter, showDeleted);
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setDeleting(false);
    }
  };

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Confirm modal
  const [confirmState, setConfirmState] = useState(null);
  const showConfirm = (title, message, onConfirm, confirmLabel, variant, regions = false) => {
    setApplyFamily(false);
    setConfirmState({ title, message, onConfirm, confirmLabel, variant, regions });
  };

  const familyMsg = (data) => {
    if (!data.results || data.results.length === 0) return "";
    return ` (+${data.results.filter((r) => r.ok).length} régions)`;
  };

  // Debounce ref
  const debounceRef = useRef(null);

  const fetchMonitors = useCallback(async () => {
    const headers = { Authorization: `Bearer ${getToken()}` };

    // Fetch monitors
    try {
      const res = await fetch(`${getHost()}/api/monitors`, { headers });
      if (res.ok) {
        const data = await res.json();
        setMonitors(data.monitors || []);
      }
    } catch { /* */ }

    // Fetch product counts
    try {
      const res = await fetch(`${getHost()}/api/admin/product-counts`, { headers });
      if (res.ok) {
        const data = await res.json();
        setProductCounts(data.counts || {});
      }
    } catch { /* */ }

    setLoadingMonitors(false);
  }, []);

  const fetchProducts = useCallback(async (monitorId, pg, searchTerm, instock, deleted) => {
    if (!monitorId) return;
    setLoadingProducts(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: PAGE_SIZE });
      if (searchTerm) params.set("search", searchTerm);
      if (instock) params.set("instock", instock);
      if (deleted) params.set("deleted", "true");
      const res = await fetch(
        `${getHost()}/api/admin/monitors/${monitorId}/products?${params}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
        setTotalProducts(data.total || 0);
        setStats({
          instock: data.instock_count || 0,
          outofstock: data.outofstock_count || 0,
          whitelisted: data.whitelisted_count || 0,
          deleted: data.deleted_count || 0,
        });
      }
    } catch {
      // Keep existing state
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  useEffect(() => {
    fetchMonitors();
  }, [fetchMonitors]);

  useEffect(() => {
    if (selectedMonitor) {
      fetchProducts(selectedMonitor, page, search, instockFilter, showDeleted);
    }
  }, [selectedMonitor, page, instockFilter, showDeleted, fetchProducts]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSiblings([]);
    setApplyFamily(false);
    setSelectedSiblingIds([]);
    if (!selectedMonitor) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${getHost()}/api/monitors/${selectedMonitor}/siblings`,
          { headers: { Authorization: `Bearer ${getToken()}` } },
        );
        const data = await res.json();
        if (cancelled) return;
        const sibs = data.siblings || [];
        setSiblings(sibs);
        setSelectedSiblingIds(sibs.map((s) => s.monitor_id));
      } catch {
        if (!cancelled) setSiblings([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedMonitor]);

  // Debounced search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchProducts(selectedMonitor, 1, value, instockFilter, showDeleted);
    }, 300);
  };

  // Close monitor dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (monitorDropdownRef.current && !monitorDropdownRef.current.contains(e.target)) {
        setShowMonitorDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMonitorSelect = (monitorId) => {
    setSelectedMonitor(monitorId);
    setShowMonitorDropdown(false);
    setMonitorSearch("");
    setSearch("");
    setInstockFilter("");
    setPage(1);
    setProducts([]);
    setTotalProducts(0);
    setStats({ instock: 0, outofstock: 0, whitelisted: 0, deleted: 0 });
    setShowDeleted(false);
    setSelected(new Set());
  };

  const refresh = useCallback(() => {
    if (selectedMonitor) {
      fetchProducts(selectedMonitor, page, search, instockFilter, showDeleted);
    }
  }, [selectedMonitor, page, search, instockFilter, showDeleted, fetchProducts]);

  const handleInstockChange = (e) => {
    setInstockFilter(e.target.value);
    setPage(1);
  };

  const toggleShowDeleted = () => {
    setShowDeleted((prev) => !prev);
    setSelected(new Set());
    setPage(1);
  };

  const handleDelete = (pid) => {
    showConfirm("Delete product", `Remove "${pid}" from tracking? It moves to the Deleted view, so the monitor won't re-add it; restore it from there.`, async (monitorIds) => {
      setConfirmState(null);
      try {
        const res = monitorIds.length > 0
          ? await fetch(
            `${getHost()}/api/admin/monitors/${selectedMonitor}/products/bulk`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${getToken()}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ pids: [pid], monitor_ids: monitorIds }),
            },
          )
          : await fetch(
            `${getHost()}/api/admin/monitors/${selectedMonitor}/products?pid=${encodeURIComponent(pid)}`,
            {
              method: "DELETE",
              headers: { Authorization: `Bearer ${getToken()}` },
            },
          );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        showToast(`Product deleted${familyMsg(data)}`);
        fetchProducts(selectedMonitor, page, search, instockFilter, showDeleted);
      } catch (error) {
        showToast(error.message, "error");
      }
    }, "Delete", "danger", true);
  };

  const handleAddProduct = async () => {
    if (!newPid.trim()) return;
    setAdding(true);
    try {
      const res = await fetch(
        `${getHost()}/api/admin/monitors/${selectedMonitor}/products`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            pid: newPid.trim(),
            ...(newMaxPrice && Number(newMaxPrice) > 0 ? { max_price: Number(newMaxPrice) } : {}),
            ...(familyIds.length > 0 ? { monitor_ids: familyIds } : {}),
          }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setNewPid("");
      setNewMaxPrice("");
      setShowAddModal(false);
      showToast(`Product added${familyMsg(data)}`);
      refresh();
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setAdding(false);
    }
  };

  const handleBulkAddProducts = async () => {
    const pids = bulkText
      .split(/[\s,]+/)
      .map((p) => p.trim())
      .filter((p) => p);
    if (pids.length === 0) return;
    setAdding(true);
    try {
      const res = await fetch(
        `${getHost()}/api/admin/monitors/${selectedMonitor}/products/bulk`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            pids,
            ...(newMaxPrice && Number(newMaxPrice) > 0 ? { max_price: Number(newMaxPrice) } : {}),
            ...(familyIds.length > 0 ? { monitor_ids: familyIds } : {}),
          }),
        },
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error);
      }
      const data = await res.json();
      setBulkText("");
      setNewMaxPrice("");
      setBulkMode(false);
      setShowAddModal(false);
      showToast(`Added ${data.upserted}, updated ${data.modified} (${data.total} total)${familyMsg(data)}`);
      refresh();
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setAdding(false);
    }
  };

  const handleImageError = (e) => {
    e.target.style.display = "none";
    e.target.nextSibling.style.display = "flex";
  };

  const getStockStatus = (product) => {
    if (product.instock === true || (product.sizes && product.sizes.length > 0)) return "instock";
    return "oos";
  };

  const renderStatusBadge = (product) => {
    const status = getStockStatus(product);
    if (status === "instock") {
      return <Badge bg="" style={{ backgroundColor: "#00E98E22", color: "#00E98E" }} className="fs-11 fw-500">In Stock</Badge>;
    }
    return <Badge bg="" style={{ backgroundColor: "#FF444422", color: "#FF4444" }} className="fs-11 fw-500">Out of Stock</Badge>;
  };

  const pollCommandStatus = async (commandId, successMsg, maxAttempts = 10) => {
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await fetch(
          `${getHost()}/api/commands/${commandId}/status`,
          { headers: { Authorization: `Bearer ${getToken()}` } },
        );
        if (!res.ok) continue;
        const data = await res.json();
        if (data.status === "completed") {
          showToast(successMsg);
          fetchProducts(selectedMonitor, page, search, instockFilter, showDeleted);
          return;
        }
        if (data.status === "error") {
          showToast(`Command failed: ${data.error || "unknown"}`, "error");
          return;
        }
      } catch {
        // keep polling
      }
    }
    showToast(`${successMsg} (still processing — check back shortly)`, "warning");
  };

  const patchProduct = async (pid, updates, successMsg) => {
    try {
      const res = await fetch(
        `${getHost()}/api/admin/monitors/${selectedMonitor}/products`,
        {
          method: "PATCH",
          headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
          body: JSON.stringify({ pid, ...updates }),
        },
      );
      if (res.ok) {
        const data = await res.json();
        if (data.broadcast_error) {
          showToast(`${successMsg} (monitor will pick up within 30s)`, "warning");
          fetchProducts(selectedMonitor, page, search, instockFilter, showDeleted);
        } else if (data.command_id) {
          showToast(`${successMsg} — waiting for monitor...`, "info");
          fetchProducts(selectedMonitor, page, search, instockFilter, showDeleted);
          pollCommandStatus(data.command_id, `${successMsg} — confirmed by monitor`);
        } else {
          if (successMsg) showToast(successMsg);
          fetchProducts(selectedMonitor, page, search, instockFilter, showDeleted);
        }
      } else {
        const data = await res.json().catch(() => ({}));
        showToast(data.error || "Update failed", "error");
      }
    } catch {
      showToast("Update failed", "error");
    }
  };

  const handleToggleInstock = (product) => {
    const newInstock = getStockStatus(product) !== "instock";
    const label = newInstock ? "Marked in stock" : "Marked out of stock";
    patchProduct(product.pid, { instock: newInstock }, label);
  };

  const handleRemoveSource = (product) => {
    showConfirm("Remove whitelist", `This product will no longer be treated as a unique. Continue?`, () => {
      setConfirmState(null);
      patchProduct(product.pid, { source: null }, "Whitelist removed");
    }, "Remove", "danger");
  };

  const renderSourceBadge = (source) => {
    if (source === "whitelist") {
      return <Badge bg="" style={{ backgroundColor: "#7C98FF22", color: "#7C98FF" }} className="fs-11 fw-500">whitelist</Badge>;
    }
    if (source === "admin") {
      return <Badge bg="" style={{ backgroundColor: "#A855F722", color: "#A855F7" }} className="fs-11 fw-500">admin</Badge>;
    }
    if (source === "discovery") {
      return <Badge bg="" style={{ backgroundColor: "#00E98E22", color: "#00E98E" }} className="fs-11 fw-500">discovery</Badge>;
    }
    if (source === "manifest") {
      return <Badge bg="" style={{ backgroundColor: "#ADADAD22", color: "#ADADAD" }} className="fs-11 fw-500">manifest</Badge>;
    }
    if (source) {
      return <Badge bg="" style={{ backgroundColor: "#ffffff14", color: "#ADADAD" }} className="fs-11 fw-500">{source}</Badge>;
    }
    return null;
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const items = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    items.push(
      <Pagination.Prev key="prev" disabled={page === 1} onClick={() => setPage(page - 1)} />,
    );
    if (start > 1) {
      items.push(<Pagination.Item key={1} onClick={() => setPage(1)}>1</Pagination.Item>);
      if (start > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
    }
    for (let i = start; i <= end; i++) {
      items.push(
        <Pagination.Item key={i} active={i === page} onClick={() => setPage(i)}>
          {i}
        </Pagination.Item>,
      );
    }
    if (end < totalPages) {
      if (end < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      items.push(
        <Pagination.Item key={totalPages} onClick={() => setPage(totalPages)}>
          {totalPages}
        </Pagination.Item>,
      );
    }
    items.push(
      <Pagination.Next key="next" disabled={page === totalPages} onClick={() => setPage(page + 1)} />,
    );

    return <Pagination className="justify-content-center mb-0 mt-3">{items}</Pagination>;
  };

  const selectedMonitorData = monitors.find((m) => m._id === selectedMonitor);

  const familyBlock = siblings.length > 0 && (
    <div className="mt-3">
      <Form.Check
        type="checkbox"
        id="apply-family"
        label={`Apply to all available regions (${siblings.length})`}
        checked={applyFamily}
        onChange={(e) => setApplyFamily(e.target.checked)}
        className="text-main fs-13"
      />
      {applyFamily && (
        <>
          <div className="d-flex justify-content-between align-items-center mt-2 ps-3">
            <span className="text-white-45 fs-12">
              {selectedSiblingIds.length}/{siblings.length} selected
            </span>
            <Button
              variant="link"
              size="sm"
              className="text-main p-0 fs-12 text-decoration-none"
              onClick={() =>
                setSelectedSiblingIds(
                  selectedSiblingIds.length === siblings.length
                    ? []
                    : siblings.map((s) => s.monitor_id),
                )
              }
            >
              {selectedSiblingIds.length === siblings.length
                ? "Deselect all"
                : "Select all"}
            </Button>
          </div>
          <div className="d-flex flex-wrap gap-2 mt-1 ps-3">
            {siblings.map((s) => {
              const sel = selectedSiblingIds.includes(s.monitor_id);
              return (
                <button
                  type="button"
                  key={s.monitor_id}
                  onClick={() =>
                    setSelectedSiblingIds((prev) =>
                      sel
                        ? prev.filter((x) => x !== s.monitor_id)
                        : [...prev, s.monitor_id],
                    )
                  }
                  className="d-flex align-items-center gap-1"
                  style={{
                    backgroundColor: sel ? "#00E98E" : "transparent",
                    border: `1px solid ${sel ? "#00E98E" : "rgba(255,255,255,0.25)"}`,
                    color: sel ? "#0b1f17" : "rgba(255,255,255,0.55)",
                    borderRadius: "999px",
                    padding: "3px 10px",
                    fontWeight: 600,
                    fontSize: "12px",
                    lineHeight: 1.4,
                  }}
                >
                  {sel ? <FiCheck /> : <FiPlus />}
                  {s.name}
                  {s.region ? ` (${s.region})` : ""}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="w-100 monitor-setting">
      <Header toggleSidebar={toggleSidebar} />
      <div className="pb-4 mx-4 content-body">
        <Row className="gx-0 align-items-center">
          <Col md={12}>
            <div className="mb-2">
              <div className="d-inline-flex align-items-center justify-content-center bg-dark rounded p-2 mb-2">
                <FiPackage className="text-white" />
              </div>
              <h4 className="fs-22 text-main mb-1">Products</h4>
              <p className="text-white-65 fs-14 mb-0">
                View and manage products tracked by each monitor
              </p>
            </div>
          </Col>
        </Row>
      </div>

      <div className="px-4 mb-4">
        {/* Monitor selector */}
        <Row className="mb-4">
          <Col md={4}>
            <Form.Label className="text-main fs-12">Monitor</Form.Label>
            {loadingMonitors ? (
              <div className="py-2">
                <Spinner size="sm" />
              </div>
            ) : (
              <div ref={monitorDropdownRef} style={{ position: "relative" }}>
                <Form.Control
                  type="text"
                  placeholder={selectedMonitor ? monitors.find(m => m._id === selectedMonitor)?.name || "Select..." : "Search monitors..."}
                  value={monitorSearch}
                  onChange={(e) => {
                    setMonitorSearch(e.target.value);
                    setShowMonitorDropdown(true);
                  }}
                  onFocus={() => setShowMonitorDropdown(true)}
                  className="form-input-field rounded-3 text-main"
                />
                {showMonitorDropdown && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      maxHeight: 300,
                      overflowY: "auto",
                      zIndex: 1000,
                      background: "#1a1a2e",
                      border: "1px solid #333",
                      borderRadius: 8,
                      marginTop: 4,
                    }}
                  >
                    {monitors
                      .filter((m) =>
                        m.name.toLowerCase().includes(monitorSearch.toLowerCase())
                      )
                      .sort((a, b) => (productCounts[b._id] || 0) - (productCounts[a._id] || 0))
                      .map((m) => (
                        <div
                          key={m._id}
                          onClick={() => handleMonitorSelect(m._id)}
                          style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            borderBottom: "1px solid #222",
                            color: selectedMonitor === m._id ? "#fff" : "#aaa",
                            background: selectedMonitor === m._id ? "#2a2a4a" : "transparent",
                          }}
                          onMouseEnter={(e) => (e.target.style.background = "#2a2a4a")}
                          onMouseLeave={(e) => (e.target.style.background = selectedMonitor === m._id ? "#2a2a4a" : "transparent")}
                        >
                          {m.name}
                          <span style={{ float: "right", color: "#666", fontSize: 12 }}>
                            {productCounts[m._id] || 0}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </Col>
        </Row>

        {!selectedMonitor ? (
          <Card className="border border-dark bg-transparent">
            <Card.Body className="p-5 text-center">
              <FiPackage className="fs-30 mb-3" style={{ color: "#848484" }} />
              <p className="text-white-65 fs-14 mb-0">
                Select a monitor to view its products
              </p>
            </Card.Body>
          </Card>
        ) : loadingProducts && products.length === 0 ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            {/* Stats row */}
            <Row className="mb-4">
              <SummaryCard
                icon={<FiPackage className="text-gray fs-14" />}
                label="Total Products"
                value={totalProducts}
              />
              <SummaryCard
                icon={<FiCheckCircle style={{ color: "#00E98E" }} className="fs-14" />}
                label="In Stock"
                value={stats.instock}
                color="#00E98E"
              />
              <SummaryCard
                icon={<FiXCircle style={{ color: "#FF4444" }} className="fs-14" />}
                label="Out of Stock"
                value={stats.outofstock}
                color={stats.outofstock > 0 ? "#FF4444" : "#ADADAD"}
              />
              <SummaryCard
                icon={<FiPackage style={{ color: "#7C98FF" }} className="fs-14" />}
                label="Whitelisted"
                value={stats.whitelisted}
                color="#7C98FF"
              />
            </Row>

            {/* Search bar + filter + add button */}
            <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
              <div className="position-relative" style={{ maxWidth: 260, flex: 1 }}>
                <FiSearch
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#848484",
                    fontSize: 13,
                  }}
                />
                <Form.Control
                  type="text"
                  placeholder="Search products..."
                  value={search}
                  onChange={handleSearchChange}
                  className="fs-12"
                  style={{
                    paddingLeft: 32,
                    backgroundColor: "transparent",
                    border: "1px solid #ffffff1f",
                    color: "#ADADAD",
                    height: 34,
                  }}
                />
              </div>
              <Form.Select
                value={instockFilter}
                onChange={handleInstockChange}
                className="form-input-field rounded-3 text-main fs-12"
                style={{ width: 140, height: 34, flex: "none" }}
              >
                <option value="">All</option>
                <option value="true">In Stock</option>
                <option value="false">Out of Stock</option>
              </Form.Select>
              <Button
                variant=""
                size="sm"
                className="rounded-3 py-1 px-2 fs-12 d-flex align-items-center"
                style={{ border: "1px solid #333", color: "#ADADAD" }}
                onClick={refresh}
                title="Refresh"
              >
                <FiRefreshCw style={{ fontSize: 13 }} />
              </Button>
              <Button
                variant="primary"
                size="sm"
                className="rounded-3 py-1 px-3 fw-600 fs-12 d-flex align-items-center gap-1"
                onClick={() => {
                  setApplyFamily(false);
                  setShowAddModal(true);
                }}
              >
                <FiPlus /> Add Product
              </Button>
              <Button
                variant="outline-warning"
                size="sm"
                className="rounded-3 py-1 px-3 fw-600 fs-12 d-flex align-items-center gap-1"
                onClick={() => setShowBlacklistModal(true)}
                disabled={!selectedMonitor}
                title="View / unblock PIDs blacklisted at the monitor level"
              >
                <FiShield /> Blacklist
              </Button>
              <Button
                variant="outline-info"
                size="sm"
                className="rounded-3 py-1 px-3 fw-600 fs-12 d-flex align-items-center gap-1"
                onClick={() => setShowCategoriesModal(true)}
                disabled={!selectedMonitor}
                title="View / edit the categories this monitor crawls"
              >
                <FiFolder /> Categories
              </Button>
              <Button
                variant={showDeleted ? "secondary" : "outline-secondary"}
                size="sm"
                className="rounded-3 py-1 px-3 fw-600 fs-12 d-flex align-items-center gap-1"
                onClick={toggleShowDeleted}
                title="Products removed from tracking. The monitor keeps skipping them until restored."
              >
                <FiArchive /> Deleted ({stats.deleted})
              </Button>
              {selected.size > 0 && (showDeleted ? (
                <Button
                  variant="success"
                  size="sm"
                  className="rounded-3 py-1 px-3 fw-600 fs-12 d-flex align-items-center gap-1"
                  onClick={() => handleRestore([...selected])}
                  disabled={deleting}
                >
                  {deleting ? <Spinner size="sm" /> : <FiRotateCcw />} Restore {selected.size}
                </Button>
              ) : (
                <Button
                  variant="danger"
                  size="sm"
                  className="rounded-3 py-1 px-3 fw-600 fs-12 d-flex align-items-center gap-1"
                  onClick={handleBulkDelete}
                  disabled={deleting}
                >
                  {deleting ? <Spinner size="sm" /> : <FiTrash2 />} Delete {selected.size}
                </Button>
              ))}
              <span className="text-white-65 fs-11 ms-auto">
                {products.length}/{totalProducts}
              </span>
            </div>

            {/* Products table */}
            {products.length === 0 ? (
              <Card className="border border-dark bg-transparent">
                <Card.Body className="p-5 text-center">
                  <p className="text-white-65 fs-14 mb-0">No products found</p>
                </Card.Body>
              </Card>
            ) : (
              <Card className="border border-dark bg-transparent">
                <Card.Body className="p-0">
                  <div className="table-mobile-scroll" style={{ width: "100%" }}>
                    <Table
                      className="table custom-table table-bordered products-table mb-0"
                      hover
                      style={{ tableLayout: "fixed", minWidth: 1000, width: "100%" }}
                    >
                      <thead>
                        <tr>
                          <th className="fs-12 fw-500 border-bottom text-center" style={{ width: 30 }}>
                            <Form.Check
                              type="checkbox"
                              checked={products.length > 0 && selected.size === products.length}
                              onChange={toggleSelectAll}
                              style={{ margin: 0 }}
                            />
                          </th>
                          <th className="fs-12 fw-500 border-bottom" style={{ width: 60 }}>Image</th>
                          <th className="fs-12 fw-500 border-bottom" style={{ width: 160 }}>PID</th>
                          <th className="fs-12 fw-500 border-bottom" style={{ width: "auto" }}>Name</th>
                          <th className="fs-12 fw-500 border-bottom" style={{ width: 75 }}>Price</th>
                          <th className="fs-12 fw-500 border-bottom" style={{ width: 95 }}>Status</th>
                          <th className="fs-12 fw-500 border-bottom" style={{ width: 90 }}>Sizes</th>
                          <th className="fs-12 fw-500 border-bottom" style={{ width: 85 }}>Source</th>
                          <th className="fs-12 fw-500 border-bottom" style={{ width: 85 }}>Updated</th>
                          <th className="fs-12 fw-500 border-bottom" style={{ width: 85 }}>Last Ping</th>
                          <th className="fs-12 fw-500 border-bottom" style={{ width: 40 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map((product) => (
                          <tr key={product.pid}>
                            {/* Checkbox */}
                            <td className="py-2 align-middle text-center">
                              <Form.Check
                                type="checkbox"
                                checked={selected.has(product.pid)}
                                onChange={() => toggleSelect(product.pid)}
                                style={{ margin: 0 }}
                              />
                            </td>
                            {/* Image */}
                            <td className="py-2 align-middle">
                              <div style={{ width: 36, height: 36, position: "relative" }}>
                                {product.image ? (
                                  <>
                                    <img
                                      src={product.image}
                                      alt=""
                                      style={{ width: 36, height: 36, borderRadius: 4, objectFit: "cover" }}
                                      onError={handleImageError}
                                    />
                                    <div
                                      style={{
                                        width: 36, height: 36, borderRadius: 4,
                                        backgroundColor: "#ffffff14", display: "none",
                                        alignItems: "center", justifyContent: "center",
                                      }}
                                    >
                                      <FiPackage style={{ color: "#848484", fontSize: 14 }} />
                                    </div>
                                  </>
                                ) : (
                                  <div
                                    style={{
                                      width: 36, height: 36, borderRadius: 4,
                                      backgroundColor: "#ffffff14", display: "flex",
                                      alignItems: "center", justifyContent: "center",
                                    }}
                                  >
                                    <FiPackage style={{ color: "#848484", fontSize: 14 }} />
                                  </div>
                                )}
                              </div>
                            </td>
                            {/* PID */}
                            <td className="text-main fs-11 py-2 align-middle" style={{ verticalAlign: "middle" }}>
                              <Copiable text={product.pid} wrap>
                                {product.pid}
                              </Copiable>
                            </td>
                            {/* Name */}
                            <td className="fs-12 py-2 align-middle">
                              <div className="d-flex align-items-start gap-1" style={{ minWidth: 0 }}>
                                <div style={{ flex: 1, minWidth: 0 }} className="text-main">
                                  <Copiable text={product.name || ""} wrap>
                                    {product.name || "—"}
                                  </Copiable>
                                </div>
                                {product.url && (
                                  <a
                                    href={product.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Open product page"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ color: "#555", flexShrink: 0, marginTop: 3 }}
                                  >
                                    <FiExternalLink style={{ fontSize: 11 }} />
                                  </a>
                                )}
                              </div>
                            </td>
                            {/* Price */}
                            <td className="text-main fs-12 py-2 align-middle">
                              {product.price ? <Copiable text={product.price}>{product.price}</Copiable> : "—"}
                            </td>
                            {/* Status */}
                            <td className="py-2 align-middle">
                              <span
                                role="button"
                                onClick={() => handleToggleInstock(product)}
                                title="Click to toggle stock status"
                                style={{ cursor: "pointer" }}
                              >
                                {renderStatusBadge(product)}
                              </span>
                            </td>
                            {/* Sizes */}
                            <td className="py-2 align-middle" style={{ position: "relative" }}>
                              {product.sizes && product.sizes.length > 0 ? (
                                <span className="d-inline-flex align-items-center gap-1">
                                  <span
                                    role="button"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setExpandedSizes(expandedSizes === product.pid ? null : product.pid)}
                                  >
                                    <Badge bg="" style={{ backgroundColor: "#ffffff14", color: "#ADADAD" }} className="fs-11 fw-500">
                                      {product.sizes.length} sizes
                                    </Badge>
                                  </span>
                                  <OverlayTrigger placement="top" overlay={<Tooltip>Clear sizes (force re-ping)</Tooltip>}>
                                    <span
                                      role="button"
                                      style={{ cursor: "pointer", color: "#FF577B", fontSize: 12 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        patchProduct(product.pid, { clear_sizes: true }, "Sizes cleared — will re-ping");
                                      }}
                                    >
                                      <FiX />
                                    </span>
                                  </OverlayTrigger>
                                  {expandedSizes === product.pid && (
                                    <div
                                      className="products-sizes-popover"
                                      style={{
                                        position: "absolute",
                                        zIndex: 10,
                                        background: "#1a1a2e",
                                        border: "1px solid #333",
                                        borderRadius: 8,
                                        padding: "8px 12px",
                                        marginTop: 4,
                                        maxHeight: 200,
                                        overflowY: "auto",
                                        minWidth: 120,
                                        right: 0,
                                      }}
                                    >
                                      {product.sizes.map((size, i) => (
                                        <div key={i} className="text-main fs-12" style={{ padding: "2px 0" }}>
                                          <Copiable text={size}>{size}</Copiable>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </span>
                              ) : (
                                <span className="text-white-65 fs-11">—</span>
                              )}
                            </td>
                            {/* Source */}
                            <td className="py-2 align-middle">
                              {product.source === "whitelist" ? (
                                <span className="d-inline-flex align-items-center gap-1">
                                  {renderSourceBadge(product.source)}
                                  <FiX
                                    role="button"
                                    style={{ fontSize: 11, color: "#7C98FF", cursor: "pointer" }}
                                    title="Remove whitelist"
                                    onClick={() => handleRemoveSource(product)}
                                  />
                                </span>
                              ) : (
                                renderSourceBadge(product.source)
                              )}
                            </td>
                            {/* Updated */}
                            <td className="py-2 align-middle">
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>{formatDate(product.updated_at)}</Tooltip>}
                              >
                                <span className="text-white-65 fs-11">{formatRelativeTime(product.updated_at)}</span>
                              </OverlayTrigger>
                            </td>
                            {/* Last Ping */}
                            <td className="py-2 align-middle">
                              {product.last_pinged ? (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>{formatDate(product.last_pinged)}</Tooltip>}
                                >
                                  <span className="text-white-65 fs-11">{formatRelativeTime(product.last_pinged)}</span>
                                </OverlayTrigger>
                              ) : (
                                <span className="text-white-65 fs-11">—</span>
                              )}
                            </td>
                            {/* Delete / Restore */}
                            <td className="py-2 align-middle text-center">
                              {showDeleted ? (
                                <Button
                                  variant=""
                                  size="sm"
                                  className="p-0"
                                  style={{ color: "#00E98E", border: "none", background: "none" }}
                                  onClick={() => handleRestore([product.pid])}
                                  disabled={deleting}
                                  title={product.deleted_at ? `Deleted ${formatDate(product.deleted_at)} — restore` : "Restore product"}
                                >
                                  <FiRotateCcw style={{ fontSize: 14 }} />
                                </Button>
                              ) : (
                                <Button
                                  variant=""
                                  size="sm"
                                  className="p-0"
                                  style={{ color: "#FF4444", border: "none", background: "none" }}
                                  onClick={() => handleDelete(product.pid)}
                                  title="Delete product"
                                >
                                  <FiTrash2 style={{ fontSize: 14 }} />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  {renderPagination()}
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Add Product Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-16 fw-600">
            Add Product{selectedMonitorData ? ` — ${selectedMonitorData.name}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-end mb-2">
            <Button
              variant="link"
              size="sm"
              className="text-white-65 p-0"
              onClick={() => {
                setBulkMode(!bulkMode);
                setBulkText("");
                setNewPid("");
              }}
            >
              {bulkMode ? "switch to single" : "bulk add"}
            </Button>
          </div>
          {bulkMode ? (
            <Form.Group>
              <Form.Label className="text-main fs-12">Product IDs (PIDs)</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder="Paste PIDs — one per line or comma-separated"
                className="form-input-field font-monospace"
              />
            </Form.Group>
          ) : (
            <Form.Group>
              <Form.Label className="text-main fs-12">Product ID (PID)</Form.Label>
              <Form.Control
                type="text"
                value={newPid}
                onChange={(e) => setNewPid(e.target.value)}
                placeholder="Enter product ID..."
                className="form-input-field"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddProduct();
                }}
              />
            </Form.Group>
          )}
          <Form.Group className="mt-3">
            <Form.Label className="text-main fs-12">Max Price (optional)</Form.Label>
            <Form.Control
              type="number"
              value={newMaxPrice}
              onChange={(e) => setNewMaxPrice(e.target.value)}
              placeholder="e.g. 150"
              className="form-input-field"
              min="0"
              step="0.01"
            />
          </Form.Group>
          {familyBlock}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAddModal(false)}
            className="rounded-3 py-2 px-3 fw-600 fs-13"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={bulkMode ? handleBulkAddProducts : handleAddProduct}
            disabled={adding || (bulkMode ? !bulkText.trim() : !newPid.trim())}
            className="rounded-3 py-2 px-3 fw-600 fs-13 d-flex align-items-center gap-2"
          >
            {adding ? <Spinner size="sm" /> : <FiPlus />}
            {bulkMode ? "Add All" : "Add"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Monitor-level blacklist modal */}
      <MonitorBlacklistModal
        show={showBlacklistModal}
        onHide={() => {
          setShowBlacklistModal(false);
          if (selectedMonitor) fetchProducts(selectedMonitor, page, search, instockFilter, showDeleted);
        }}
        monitor={selectedMonitorData}
      />

      <MonitorCategoriesModal
        show={showCategoriesModal}
        onHide={() => setShowCategoriesModal(false)}
        monitor={selectedMonitorData}
      />

      {/* Confirm modal */}
      {confirmState && (
        <ConfirmModal
          show
          title={confirmState.title}
          message={confirmState.message}
          onConfirm={() => confirmState.onConfirm(familyIds)}
          onCancel={() => setConfirmState(null)}
          confirmLabel={confirmState.confirmLabel}
          variant={confirmState.variant}
        >
          {confirmState.regions && familyBlock}
        </ConfirmModal>
      )}

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Pagination CSS override for dark theme */}
      <style>{`
        .page-item .page-link {
          background-color: transparent;
          border-color: #333;
          color: #ADADAD;
          font-size: 12px;
        }
        .page-item.active .page-link {
          background-color: #2a2a4a;
          border-color: #555;
          color: #fff;
        }
        .page-item.disabled .page-link {
          background-color: transparent;
          border-color: #222;
          color: #555;
        }
        .page-item .page-link:hover {
          background-color: #2a2a4a;
          border-color: #555;
          color: #fff;
        }
        .page-item .page-link:focus {
          box-shadow: none;
        }
      `}</style>
    </div>
  );
};

export default AdminProducts;
